import { BODY_BACKGROUND, BORDER, DARK_MODE, GRAY_SCALE, THEME_COLOR } from 'constants/ThemeConstant'

export function rgba(hex, opacity = 1) {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    throw new Error('Invalid Hex')
  }

  const decimal = parseInt(hex.substring(1), 16)

  const r = (decimal >> 16) & 255
  const g = (decimal >> 8) & 255
  const b = decimal & 255

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export const baseTheme = {
  borderRadius: 4,
  lineWidth: 0.1,
  colorPrimary: THEME_COLOR.BLACK,
  colorSuccess: THEME_COLOR.CYAN,
  colorWarning: THEME_COLOR.GOLD,
  colorError: THEME_COLOR.VOLCANO,
  colorInfo: THEME_COLOR.BLUE,
  colorText: GRAY_SCALE.GRAY,
  colorBorder: BORDER.BASE_COLOR,
  colorBgBody: BODY_BACKGROUND,
  colorTextDisabled: 'rgba(0, 0, 0, 0.55)',
  controlHeight: 24,
  controlHeightLG: 32,
  controlHeightSM: 24,
  fontFamily: 'KiaSignature',
  // fontFamily: `HDharmony, 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
  //   'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
  //   'Noto Color Emoji'`,
  fontSize: 12,
  fontSizeHeading2: 22,
  fontSizeHeading4: 17,
}

const getBaseComponentThemeConfig = ({
  Button = {},
  Menu = {},
  Card = {},
  Table = {},
  Select = {},
  DatePicker = {},
  Layout = {},
  Input = {},
  Dropdown = {},
  Calendar = {},
  Form = {},
  Avatar = {},
  Switch = {},
  Tabs = {},
  Descriptions = {},
} = {}) => {
  return {
    Button: {
      ...Button,
    },
    Dropdown: {
      controlPaddingHorizontal: 20,
      borderRadiusLG: 10,
      paddingXXS: '4px 0px',
      ...Dropdown,
    },
    Calendar: {
      ...Calendar,
    },
    Checkbox: {
      controlInteractiveSize: 16,
      lineWidth: 2,
      borderRadiusSM: 4,
    },
    Card: {
      colorTextHeading: GRAY_SCALE.GRAY_DARK,
      paddingLG: 20,
      borderRadiusLG: 0,
      ...Card,
    },
    Layout: {
      bodyBg: BODY_BACKGROUND,
      headerBg: GRAY_SCALE.WHITE,
      ...Layout,
    },
    Breadcrumb: {
      colorTextDescription: GRAY_SCALE.GRAY_LIGHT,
      colorText: baseTheme.colorPrimary,
      colorBgTextHover: 'transparent',
    },
    Menu: {
      itemBg: 'transparent',
      activeBarHeight: -1,
      activeBarWidth: 3,
      horizontalItemSelectedColor: baseTheme.colorPrimary,
      itemHoverColor: baseTheme.colorText,
      itemSelectedColor: rgba(baseTheme.colorPrimary, 0.7),
      itemSelectedBg: rgba(baseTheme.colorPrimary, 0.1),
      itemHoverBg: 'transparent',
      radiusItem: 0,
      subMenuItemBg: 'transparent',
      itemMarginInline: 0,
      controlHeightLG: 40,
      controlHeightSM: 22,
      ...Menu,
    },
    Input: {
      ...Input,
    },
    Pagination: {
      paginationItemSize: 30,
      borderRadius: '50%',
      colorBgContainer: baseTheme.colorPrimary,
      colorPrimary: GRAY_SCALE.WHITE,
      colorPrimaryHover: GRAY_SCALE.WHITE,
    },
    Steps: {
      wireframe: true,
      controlHeight: 32,
      waitIconColor: GRAY_SCALE.GRAY_LIGHT,
    },
    DatePicker: {
      controlItemBgActive: rgba(baseTheme.colorPrimary, 0.1),
      borderRadiusSM: 6,
      borderRadius: 6,
      lineWidthBold: 0,
      ...DatePicker,
    },
    Radio: {
      fontSizeLG: 14,
    },
    Slider: {
      colorPrimaryBorder: rgba(baseTheme.colorPrimary, 0.8),
      colorPrimaryBorderHover: baseTheme.colorPrimary,
    },
    Select: {
      paddingXXS: '4px 0px',
      // controlHeight: 40,
      // controlHeightSM: 24,
      controlItemBgActive: rgba(baseTheme.colorPrimary, 0.1),
      ...Select,
    },
    TreeSelect: {
      // controlHeightSM: 24,
    },
    Tooltip: {
      colorBgDefault: rgba(GRAY_SCALE.DARK, 0.75),
      controlHeight: 32,
    },
    Timeline: {
      lineType: 'dashed',
    },
    Tag: {
      lineHeight: 2.1,
    },
    Table: {
      lineWidth: 1,
      lineHeight: 2,
      colorText: GRAY_SCALE.GRAY,
      borderRadiusLG: 0,
      ...Table,
    },
    Form: {
      ...Form,
    },
    Avatar: {
      controlHeight: 30,
      controlHeightSM: 30,
      ...Avatar,
    },
    Switch: {
      controlHeight: 30,
      controlHeightSM: 30,
      ...Switch,
    },
    Tabs: {
      cardHeight: 34,
      borderRadius: 0,
      ...Tabs,
    },
    Descriptions: {
      ...Descriptions,
    },
  }
}

export const lightTheme = {
  token: {
    ...baseTheme,
    colorBorderSecondary: BORDER.SYSTEM_COLOR,
    colorTextBase: GRAY_SCALE.GRAY,
  },
  components: getBaseComponentThemeConfig({
    Tabs: {
      cardBg: rgba('#05141f', 0.1),
      itemColor: '#636d74',
      itemSelectedColor: '#fff',
      colorBgContainer: rgba('#05141f', 0.7),
      colorTextDescription: '#9eaeb6',
    },
    Button: {
      colorPrimary: '#05141f',
      colorPrimaryHover: '#727375',
    },
    Card: {
      colorBgContainer: '#eaecee',
    },
    Table: {
      headerColor: THEME_COLOR.TABLE_HEADER_COLOR,
      headerBg: THEME_COLOR.TABLE_HEADER_BG,
      fixedHeaderSortActiveBg: '#727375',
      headerSortActiveBg: '#727375',
      headerSortHoverBg: '#727375',
    },
  }),
}

export const darkTheme = {
  token: {
    ...baseTheme,
    colorTextBase: DARK_MODE.TEXT_COLOR,
    colorBgBase: DARK_MODE.BG_COLOR,
    colorText: DARK_MODE.TEXT_COLOR,
    colorTextDisabled: rgba(DARK_MODE.TEXT_COLOR, 0.5),
    colorBorder: DARK_MODE.BORDER_BASE_COLOR,
    colorBorderSecondary: rgba(DARK_MODE.BORDER_BASE_COLOR, 0.5),
    colorTextHeading: DARK_MODE.HEADING_COLOR,

    optionActiveBg: DARK_MODE.SELECT_HOVER_COLOR,
    optionSelectedColor: DARK_MODE.BRIGHT_TEXT_COLOR,
    colorTextPlaceholder: DARK_MODE.DISABLED_SELECT_TEXT_COLOR,
    colorTextQuaternary: DARK_MODE.DISABLED_SELECT_TEXT_COLOR, //select box 우측 화살표
    controlOutline: 'transparent',
    controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
    controlItemBgHover: DARK_MODE.ACTIVE_BG_COLOR,
    boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
  },
  components: getBaseComponentThemeConfig({
    Tabs: {
      itemColor: DARK_MODE.DISABLED_TEXT_COLOR,
      itemActiveColor: DARK_MODE.DISABLED_TEXT_COLOR,
      itemHoverColor: DARK_MODE.DISABLED_TEXT_COLOR,
      itemSelectedColor: DARK_MODE.BRIGHT_TEXT_COLOR,
    },

    Button: {
      controlOutline: 'transparent',
      colorBorder: DARK_MODE.BORDER_BASE_COLOR,
      colorText: DARK_MODE.TEXT_COLOR,
      colorTextDisabled: DARK_MODE.DISABLED_TEXT_COLOR,
      ghostBg: DARK_MODE.WHITE_COLOR,
    },
    Descriptions: {
      colorTextTertiary: DARK_MODE.BRIGHT_TEXT_COLOR,
    },
    //     Card: {
    //         colorTextHeading: DARK_MODE.HEADING_COLOR,
    //     },
    Layout: {
      bodyBg: '#1b2531',
      headerBg: DARK_MODE.BG_COLOR,
    },
    Menu: {
      itemSelectedBg: 'transparent',
      horizontalItemSelectedColor: GRAY_SCALE.WHITE,
      itemHoverColor: GRAY_SCALE.WHITE,
      itemSelectedColor: GRAY_SCALE.WHITE,
      itemColor: DARK_MODE.TEXT_COLOR,
      colorActiveBarWidth: 0,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    //     Dropdown: {
    //         optionActiveBg:DARK_MODE.SELECT_HOVER_COLOR,
    //         optionSelectedColor:DARK_MODE.BRIGHT_TEXT_COLOR,
    //         colorTextPlaceholder:DARK_MODE.DISABLED_SELECT_TEXT_COLOR,
    //         colorText: DARK_MODE.DARK_TEXT_COLOR,
    //         colorTextQuaternary:DARK_MODE.BRIGHT_TEXT_COLOR,
    //         colorBgContainer: DARK_MODE.WHITE_COLOR,
    //         controlOutline: 'transparent',
    //         controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
    //         controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
    //         boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    //         colorBgElevated: DARK_MODE.WHITE_COLOR,
    //     },
    //     Select: {
    //         optionActiveBg:DARK_MODE.SELECT_HOVER_COLOR,
    //         optionSelectedColor:DARK_MODE.BRIGHT_TEXT_COLOR,
    //         colorTextPlaceholder:DARK_MODE.DISABLED_SELECT_TEXT_COLOR,
    //         colorText: DARK_MODE.DARK_TEXT_COLOR,
    //         colorTextQuaternary:DARK_MODE.DISABLED_SELECT_TEXT_COLOR,
    //         colorBgContainer: DARK_MODE.WHITE_COLOR,
    //         controlOutline: 'transparent',
    //         controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
    //         controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
    //         boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    //         colorBgElevated: DARK_MODE.WHITE_COLOR,
    //     },
    //     Calendar: {
    //         controlItemBgActive: '#303a4e',
    //     },
    //     Input: {
    //         colorText: DARK_MODE.DARK_TEXT_COLOR,
    //         colorTextDisabled:DARK_MODE.DISABLED_TEXT_COLOR,
    //         colorBgContainer: DARK_MODE.WHITE_COLOR,
    //         controlOutline: 'transparent',
    //     },
    //     DatePicker: {
    //         cellHoverBg: '#fff',
    //         colorText: DARK_MODE.DARK_TEXT_COLOR,
    //         colorTextDisabled: '#efe',
    //         colorBgElevated: DARK_MODE.WHITE_COLOR,
    //         colorIcon: DARK_MODE.DARK_TEXT_COLOR,
    //         colorIconHover: DARK_MODE.BRIGHT_TEXT_COLOR,
    //         colorTextHeading: DARK_MODE.DARK_TEXT_COLOR,
    //         colorTextLightSolid: '#fff',
    //         colorBgContainer: DARK_MODE.WHITE_COLOR,
    //         controlOutline: 'transparent',
    //         boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    //         controlItemBgActive: DARK_MODE.DISABLED_TEXT_COLOR,
    //         controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
    //     },
    Table: {
      colorText: DARK_MODE.TEXT_COLOR,
      colorTextHeading: DARK_MODE.HEADING_COLOR,
    },
    // }),
  }),
}
